import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "./Logo";
import AvatarSVG from "./AvatarSVG";
import { AuthService } from "../services/auth";
import PrefetchLink from "./PrefetchLink";

const DEFAULT_LINKS = [
  { key: "home", to: "/", label: "Accueil" },
  { key: "achat", to: "/achat", label: "Achat" },
  { key: "location", to: "/location", label: "Location" },
  { key: "offers", to: "/mes-annonces", label: "Mes offres" },
  { key: "add", to: "/ajouter", label: "Ajouter" },
  { key: "profile", to: "/profil", label: "Profil" },
  { key: "contact", to: "/contact", label: "Contact" },
];

/* -------------------------------------------------------------------------- */
/*                                User Menu                                   */
/* -------------------------------------------------------------------------- */
function UserMenu({ user, onSignIn }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const nav = useNavigate();

  // Fermer au clic extérieur + Esc
  useEffect(() => {
    const onClickOutside = (e) => {
      if (!open) return;
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const handleLogout = async () => {
    try {
      setBusy(true);
      await AuthService.logout();
      window.location.href = "/connexion";
    } finally {
      setBusy(false);
    }
  };

  if (!user) {
    return (
      <div className="hidden sm:flex items-center gap-3">
        <button onClick={onSignIn} className="btn-primary">Connexion</button>
      </div>
    );
  }

  return (
    <div className="hidden sm:flex items-center">
      <div className="relative">
        <button
          ref={btnRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 rounded-full px-2 py-1 hover:bg-black/5 dark:hover:bg-white/10 focus:outline-none"
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <AvatarSVG name={user.name || "Utilisateur"} className="w-9 h-9" />
          <span className="hidden xl:inline text-sm">{user.name}</span>
          <svg className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} viewBox="0 0 20 20" fill="currentColor">
            <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.17l3.71-2.94a.75.75 0 111.04 1.08l-4.24 3.36a.75.75 0 01-.94 0L5.21 8.31a.75.75 0 01.02-1.1z" />
          </svg>
        </button>

        <div
          ref={menuRef}
          role="menu"
          aria-label="User menu"
          className={`absolute right-0 mt-2 w-56 rounded-2xl border bg-white shadow-lg dark:bg-neutral-900 dark:border-neutral-800 overflow-hidden transition
          ${open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-1 pointer-events-none"}`}
        >
          <div className="bg-gray-100">
            <NavLink
              to="/profil"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-blue-100 dark:hover:bg-blue-800"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" d="M20 21a8 8 0 10-16 0" />
                <circle cx="12" cy="8" r="4" strokeWidth="1.6" />
              </svg>
              <span>Profil</span>
            </NavLink>

            <button
              role="menuitem"
              onClick={handleLogout}
              disabled={busy}
              className="w-full flex items-center gap-3 px-4 py-2 text-left text-sm hover:bg-blue-100 dark:hover:bg-blue-800 disabled:opacity-60"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" d="M9 16l-4-4 4-4" />
                <path strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" d="M5 12h11" />
                <path strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" d="M13 5h3a3 3 0 013 3v8a3 3 0 01-3 3h-3" />
              </svg>
              <span>{busy ? "Déconnexion..." : "Se déconnecter"}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                   Header                                   */
/* -------------------------------------------------------------------------- */
export default function Header({
  links = DEFAULT_LINKS,
  visibleKeys,
  user = null,
  onSignIn,
  className = "",
  sticky = true,
}) {
  const [open, setOpen] = useState(false);

  const filtered =
    visibleKeys && visibleKeys.length
      ? links.filter((l) => visibleKeys.includes(l.key))
      : links;

  const handleNavClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const linkBase =
    "px-2 py-2 rounded-lg transition will-change-transform hover:text-blue-700";
  const linkActive = "text-blue-700 font-medium";

  return (
    <header
      className={`${className} ${
        sticky ? "justify-center sticky top-0 z-40 bg-white/80 backdrop-blur border-b" : ""
      }`}
    >
      <div className="max-w-7xl mx-auto h-16 px-4 flex items-center justify-between">
        {/* Logo + marque */}
        <NavLink to="/" onClick={handleNavClick} className="flex items-center gap-2 group">
          <Logo className="w-12 h-12" />
          <span className="font-semibold tracking-tight group-hover:text-blue-700 transition">2S-PAY</span>
        </NavLink>

        {/* Nav desktop */}
        <nav className="hidden lg:flex items-center gap-6 text-sm">
          {filtered.map((l) => (
            <PrefetchLink
              key={l.key}
              to={l.to}
              onClick={handleNavClick}
              className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`}
            >
              <span className="relative group">
                {l.label}
                <span className="absolute left-0 -bottom-1 h-0.5 w-0 bg-blue-700 transition-all duration-300 group-hover:w-full" />
              </span>
            </PrefetchLink>
          ))}
        </nav>

        <UserMenu user={user} onSignIn={onSignIn} />

        {/* Burger */}
        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden rounded-lg p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          aria-label="Menu"
          aria-expanded={open}
        >
          <svg viewBox="0 0 24 24" className="w-6 h-6 stroke-gray-800" fill="none" strokeWidth="2">
            <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {/* Drawer mobile */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="lg:hidden border-t"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="container-padded px-4 py-3">
              <nav className="grid gap-1 text-sm">
                {filtered.map((l) => (
                  <PrefetchLink
                    key={l.key}
                    to={l.to}
                    onClick={() => {
                      setOpen(false);
                      handleNavClick();
                    }}
                    className={({ isActive }) =>
                      `px-2 py-2 rounded-lg ${
                        isActive ? "bg-blue-50 text-blue-700" : "hover:bg-gray-50"
                      }`
                    }
                  >
                    {l.label}
                  </PrefetchLink>
                ))}
              </nav>

              <div className="mt-3 flex gap-2">
                {user ? (
                  <>
                    <NavLink
                      to="/profil"
                      className="btn-ghost flex-1"
                      onClick={() => {
                        setOpen(false);
                        handleNavClick();
                      }}
                    >
                      Mon profil
                    </NavLink>
                    <button
                      className="btn-primary flex-1"
                      onClick={async () => {
                        try {
                          await AuthService.logout();
                          window.location.href = "/connexion";
                        } catch (_) {}
                      }}
                    >
                      Se déconnecter
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={onSignIn} className="btn-ghost flex-1">
                      Connexion
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
